import React, { useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { PlusIcon } from 'lucide-react'
import {
  buildLeadCustomerOptions,
  filterLeadCustomerOptions,
  findLeadCustomerOption,
  LeadCustomerEntity,
  LeadCustomerSelection,
} from '../lib/leadCustomerSearch'

const CREATE_ACTION_COUNT = 2

export interface LeadCustomerSearchSelectProps {
  leads: LeadCustomerEntity[]
  customers: LeadCustomerEntity[]
  value: LeadCustomerSelection
  onChange: (value: LeadCustomerSelection) => void
  disabled?: boolean
  required?: boolean
  label?: string
  placeholder?: string
  id?: string
  onCreateLead?: () => void
  onCreateCustomer?: () => void
}

export function LeadCustomerSearchSelect({
  leads,
  customers,
  value,
  onChange,
  disabled = false,
  required = false,
  label = 'Lead or Customer',
  placeholder = 'Type to search leads and customers...',
  id = 'lead-customer-search',
  onCreateLead,
  onCreateCustomer,
}: LeadCustomerSearchSelectProps) {
  const navigate = useNavigate()
  const containerRef = useRef<HTMLDivElement>(null)
  const inputRef = useRef<HTMLInputElement>(null)
  const [isOpen, setIsOpen] = useState(false)
  const [query, setQuery] = useState('')
  const [highlightedIndex, setHighlightedIndex] = useState(0)

  const allOptions = useMemo(() => buildLeadCustomerOptions(leads, customers), [leads, customers])
  const selectedOption = useMemo(
    () => findLeadCustomerOption(allOptions, value),
    [allOptions, value]
  )
  const filteredOptions = useMemo(
    () => filterLeadCustomerOptions(allOptions, query),
    [allOptions, query]
  )
  const totalNavigableItems = filteredOptions.length + CREATE_ACTION_COUNT
  const createLeadIndex = filteredOptions.length
  const createCustomerIndex = filteredOptions.length + 1

  useEffect(() => {
    if (!isOpen) {
      setQuery(selectedOption?.label ?? '')
    }
  }, [selectedOption, isOpen])

  useEffect(() => {
    setHighlightedIndex(0)
  }, [query, isOpen])

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsOpen(false)
        setQuery(selectedOption?.label ?? '')
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [selectedOption])

  const handleSelect = (option: { type: 'lead' | 'customer'; id: string; label: string }) => {
    onChange({ type: option.type, id: option.id })
    setQuery(option.label)
    setIsOpen(false)
  }

  const handleCreateLead = () => {
    setIsOpen(false)
    if (onCreateLead) {
      onCreateLead()
    } else {
      navigate('/leads/create')
    }
  }

  const handleCreateCustomer = () => {
    setIsOpen(false)
    if (onCreateCustomer) {
      onCreateCustomer()
    } else {
      navigate('/customers/create')
    }
  }

  const handleActionAtIndex = (index: number) => {
    if (index < filteredOptions.length) {
      handleSelect(filteredOptions[index])
    } else if (index === createLeadIndex) {
      handleCreateLead()
    } else if (index === createCustomerIndex) {
      handleCreateCustomer()
    }
  }

  const handleClear = () => {
    onChange(null)
    setQuery('')
    setIsOpen(false)
    inputRef.current?.focus()
  }

  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (disabled) return

    if (event.key === 'ArrowDown') {
      event.preventDefault()
      if (!isOpen) {
        setIsOpen(true)
        return
      }
      setHighlightedIndex((prev) => Math.min(prev + 1, totalNavigableItems - 1))
      return
    }

    if (event.key === 'ArrowUp') {
      event.preventDefault()
      setHighlightedIndex((prev) => Math.max(prev - 1, 0))
      return
    }

    if (event.key === 'Enter') {
      event.preventDefault()
      if (isOpen) {
        handleActionAtIndex(highlightedIndex)
      }
      return
    }

    if (event.key === 'Escape') {
      event.preventDefault()
      setIsOpen(false)
      setQuery(selectedOption?.label ?? '')
    }
  }

  const listboxId = `${id}-listbox`

  return (
    <div ref={containerRef} className="relative">
      {label && (
        <label htmlFor={id} className="block text-sm font-medium text-gray-700 mb-2">
          {label}
          {required && <span className="text-red-500"> *</span>}
        </label>
      )}
      <div className="relative">
        <input
          ref={inputRef}
          id={id}
          type="text"
          role="combobox"
          aria-expanded={isOpen}
          aria-controls={listboxId}
          aria-autocomplete="list"
          value={query}
          disabled={disabled}
          placeholder={placeholder}
          onChange={(e) => {
            setQuery(e.target.value)
            setIsOpen(true)
            if (!e.target.value.trim() && value) {
              onChange(null)
            }
          }}
          onFocus={() => {
            if (!disabled) setIsOpen(true)
          }}
          onKeyDown={handleKeyDown}
          className={`w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
            disabled ? 'bg-gray-100 cursor-not-allowed' : ''
          }`}
        />
        {value && !disabled && (
          <button
            type="button"
            onClick={handleClear}
            className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 text-sm"
            aria-label="Clear selection"
          >
            ×
          </button>
        )}
      </div>

      {isOpen && !disabled && (
        <ul
          id={listboxId}
          role="listbox"
          className="absolute z-20 mt-1 w-full max-h-60 overflow-y-auto bg-white border border-gray-200 rounded-lg shadow-lg"
        >
          {filteredOptions.length === 0 ? (
            <li className="px-3 py-2 text-sm text-gray-500">No matches found</li>
          ) : (
            filteredOptions.map((option, index) => (
              <li
                key={`${option.type}:${option.id}`}
                role="option"
                aria-selected={highlightedIndex === index}
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => handleSelect(option)}
                onMouseEnter={() => setHighlightedIndex(index)}
                className={`px-3 py-2 cursor-pointer flex items-center justify-between gap-2 ${
                  highlightedIndex === index ? 'bg-blue-50' : 'hover:bg-gray-50'
                }`}
              >
                <div className="min-w-0">
                  <p className="text-sm text-gray-900 truncate">{option.label}</p>
                  {option.email && (
                    <p className="text-xs text-gray-500 truncate">{option.email}</p>
                  )}
                </div>
                <span
                  className={`shrink-0 px-2 py-0.5 text-xs font-medium rounded-full ${
                    option.type === 'lead'
                      ? 'bg-blue-100 text-blue-800'
                      : 'bg-green-100 text-green-800'
                  }`}
                >
                  {option.type === 'lead' ? 'Lead' : 'Customer'}
                </span>
              </li>
            ))
          )}

          <li className="border-t border-gray-100" role="presentation" />
          <li
            role="option"
            aria-selected={highlightedIndex === createLeadIndex}
            onMouseDown={(e) => e.preventDefault()}
            onClick={handleCreateLead}
            onMouseEnter={() => setHighlightedIndex(createLeadIndex)}
            className={`px-3 py-2 cursor-pointer flex items-center gap-2 text-sm font-medium text-blue-600 ${
              highlightedIndex === createLeadIndex ? 'bg-blue-50' : 'hover:bg-gray-50'
            }`}
          >
            <PlusIcon className="h-4 w-4 shrink-0" />
            Create new lead
          </li>
          <li
            role="option"
            aria-selected={highlightedIndex === createCustomerIndex}
            onMouseDown={(e) => e.preventDefault()}
            onClick={handleCreateCustomer}
            onMouseEnter={() => setHighlightedIndex(createCustomerIndex)}
            className={`px-3 py-2 cursor-pointer flex items-center gap-2 text-sm font-medium text-green-700 ${
              highlightedIndex === createCustomerIndex ? 'bg-green-50' : 'hover:bg-gray-50'
            }`}
          >
            <PlusIcon className="h-4 w-4 shrink-0" />
            Create new customer
          </li>
        </ul>
      )}
    </div>
  )
}
